import { useState, useCallback, useRef, useEffect } from 'react';
import FibLevelsEditor from './FibLevelsEditor';

/**
 * TradingView-style vertical left sidebar for drawing tools.
 * Groups with expandable flyout submenus on hover.
 */

const TOOL_GROUPS = [
  {
    id: 'cursor',
    title: 'Cursor',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <path d="M4 2l10 8-5 1.5L7 16z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    tools: [
      { id: null, title: 'Cursor (default)', icon: <svg width="16" height="16" viewBox="0 0 20 20"><path d="M4 2l10 8-5 1.5L7 16z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg> },
    ],
  },
  {
    id: 'lines',
    title: 'Lines',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <line x1="3" y1="16" x2="17" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    tools: [
      { id: 'trendline', title: 'Trend Line', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="13" x2="14" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg> },
      { id: 'ray', title: 'Ray', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="12" x2="14" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="14" cy="4" r="1.5" fill="currentColor"/></svg> },
      { id: 'hline', title: 'Horizontal Line', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="1" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"/></svg> },
      { id: 'vline', title: 'Vertical Line', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="8" y1="1" x2="8" y2="15" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"/></svg> },
      { id: 'channel', title: 'Parallel Channel', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="1" y1="11" x2="15" y2="5" stroke="currentColor" strokeWidth="1.2"/><line x1="1" y1="7" x2="15" y2="1" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 2"/></svg> },
      { id: 'flatChannel', title: 'Flat Top/Bottom', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="1" y1="4" x2="15" y2="4" stroke="currentColor" strokeWidth="1.2"/><line x1="1" y1="12" x2="15" y2="8" stroke="currentColor" strokeWidth="1.2"/></svg> },
      { id: 'disjointChannel', title: 'Disjoint Channel', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="1" y1="5" x2="7" y2="3" stroke="currentColor" strokeWidth="1.2"/><line x1="1" y1="11" x2="7" y2="9" stroke="currentColor" strokeWidth="1.2"/><line x1="9" y1="7" x2="15" y2="5" stroke="currentColor" strokeWidth="1.2"/><line x1="9" y1="13" x2="15" y2="11" stroke="currentColor" strokeWidth="1.2"/></svg> },
      { id: 'regressionTrend', title: 'Regression Trend', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="12" x2="14" y2="4" stroke="currentColor" strokeWidth="1.2"/><line x1="2" y1="9" x2="14" y2="1" stroke="currentColor" strokeWidth="0.8" strokeDasharray="2 2"/><line x1="2" y1="15" x2="14" y2="7" stroke="currentColor" strokeWidth="0.8" strokeDasharray="2 2"/></svg> },
    ],
  },
  {
    id: 'fib',
    title: 'Fibonacci',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <line x1="2" y1="3" x2="18" y2="3" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
        <line x1="2" y1="7" x2="18" y2="7" stroke="currentColor" strokeWidth="1" opacity="0.4" strokeDasharray="2 2"/>
        <line x1="2" y1="10" x2="18" y2="10" stroke="currentColor" strokeWidth="1.5"/>
        <line x1="2" y1="13" x2="18" y2="13" stroke="currentColor" strokeWidth="1" opacity="0.4" strokeDasharray="2 2"/>
        <line x1="2" y1="17" x2="18" y2="17" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
      </svg>
    ),
    tools: [
      { id: 'fibonacci', title: 'Fib Retracement', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="1" y1="2" x2="15" y2="2" stroke="currentColor" strokeWidth="1" opacity="0.5"/><line x1="1" y1="5.5" x2="15" y2="5.5" stroke="currentColor" strokeWidth="1" opacity="0.4" strokeDasharray="2 2"/><line x1="1" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.5"/><line x1="1" y1="10.5" x2="15" y2="10.5" stroke="currentColor" strokeWidth="1" opacity="0.4" strokeDasharray="2 2"/><line x1="1" y1="14" x2="15" y2="14" stroke="currentColor" strokeWidth="1" opacity="0.5"/></svg> },
      { id: 'fibExtension', title: 'Fib Extension', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="1" y1="3" x2="15" y2="3" stroke="currentColor" strokeWidth="1"/><line x1="1" y1="7" x2="15" y2="7" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><line x1="1" y1="10" x2="15" y2="10" stroke="currentColor" strokeWidth="1.5"/><line x1="1" y1="14" x2="15" y2="14" stroke="currentColor" strokeWidth="1" opacity="0.6"/><text x="12" y="13" fill="currentColor" fontSize="6" opacity="0.7">E</text></svg> },
      { id: 'fibFan', title: 'Fib Fan', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="14" x2="14" y2="2" stroke="currentColor" strokeWidth="1.2"/><line x1="2" y1="14" x2="14" y2="6" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="2" y1="14" x2="14" y2="10" stroke="currentColor" strokeWidth="0.8" opacity="0.3"/></svg> },
      { id: 'fibArcs', title: 'Fib Arcs', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M4 12 A5 5 0 0 1 12 4" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5"/><path d="M5 13 A7 7 0 0 1 13 5" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.3"/><line x1="3" y1="13" x2="13" y2="3" stroke="currentColor" strokeWidth="1.2"/></svg> },
      { id: 'fibTimeZones', title: 'Fib Time Zones', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="1" x2="2" y2="15" stroke="currentColor" strokeWidth="1"/><line x1="4" y1="1" x2="4" y2="15" stroke="currentColor" strokeWidth="0.8" opacity="0.6"/><line x1="7" y1="1" x2="7" y2="15" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/><line x1="12" y1="1" x2="12" y2="15" stroke="currentColor" strokeWidth="0.8" opacity="0.3"/></svg> },
      { id: 'pitchfork', title: "Andrews' Pitchfork", icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="8" x2="14" y2="4" stroke="currentColor" strokeWidth="1.2"/><line x1="8" y1="14" x2="14" y2="1" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><line x1="8" y1="14" x2="14" y2="7" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/></svg> },
    ],
  },
  {
    id: 'shapes',
    title: 'Shapes',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <rect x="3" y="4" width="14" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    tools: [
      { id: 'rectangle', title: 'Rectangle', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="2" y="3" width="12" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg> },
      { id: 'filledRect', title: 'Filled Rectangle', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="2" y="3" width="12" height="10" rx="1" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="1.2"/></svg> },
      { id: 'triangle', title: 'Triangle', icon: <svg width="16" height="16" viewBox="0 0 16 16"><polygon points="8,2 14,14 2,14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg> },
      { id: 'ellipse', title: 'Ellipse', icon: <svg width="16" height="16" viewBox="0 0 16 16"><ellipse cx="8" cy="8" rx="6" ry="4" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg> },
      { id: 'filledEllipse', title: 'Filled Ellipse', icon: <svg width="16" height="16" viewBox="0 0 16 16"><ellipse cx="8" cy="8" rx="6" ry="4" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="1.2"/></svg> },
      { id: 'circle', title: 'Circle', icon: <svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg> },
      { id: 'arrowLine', title: 'Arrow', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="13" x2="12" y2="4" stroke="currentColor" strokeWidth="1.5"/><polygon points="13,3 9,4 12,7" fill="currentColor"/></svg> },
      { id: 'arc', title: 'Arc', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M3 12 Q8 2 13 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg> },
    ],
  },
  {
    id: 'brushes',
    title: 'Brushes',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <path d="M3 15 Q5 8 8 10 T13 6 T17 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    tools: [
      { id: 'brush', title: 'Brush', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 12 Q4 6 7 8 T12 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg> },
      { id: 'highlighter', title: 'Highlighter', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 10 Q5 4 8 8 T14 6" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" opacity="0.35"/></svg> },
      { id: 'polyline', title: 'Polyline', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 12l3-5 3 3 3-4 3 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><circle cx="2" cy="12" r="1.5" fill="currentColor"/><circle cx="5" cy="7" r="1.5" fill="currentColor"/><circle cx="8" cy="10" r="1.5" fill="currentColor"/><circle cx="11" cy="6" r="1.5" fill="currentColor"/><circle cx="14" cy="8" r="1.5" fill="currentColor"/></svg> },
    ],
  },
  {
    id: 'patterns',
    title: 'Patterns',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <path d="M2 14l4-6 4 4 4-8 4 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
      </svg>
    ),
    tools: [
      { id: 'xabcd', title: 'XABCD Pattern', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 10l3-6 3 4 3-5 3 7" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><text x="1" y="12" fill="currentColor" fontSize="5" opacity="0.6">X</text></svg> },
      { id: 'abcPattern', title: 'ABC Pattern', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 12l5-8 4 5 3-4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><text x="6" y="5" fill="currentColor" fontSize="5" opacity="0.7">B</text></svg> },
      { id: 'headShoulders', title: 'Head & Shoulders', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M1 12l2-3 2 3 2-7 2 7 2-3 2 3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg> },
      { id: 'cypherPattern', title: 'Cypher Pattern', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 11l3-7 3 5 3-3 3 5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><text x="1" y="13" fill="currentColor" fontSize="5" opacity="0.6">C</text></svg> },
      { id: 'triPattern', title: 'Triangle Pattern', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 12l4-7 4 3 4-2" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M2 12l12-6" fill="none" stroke="currentColor" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5"/></svg> },
      { id: 'threeDrives', title: '3 Drives', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M1 12l2-4 2 3 2-5 2 3 2-7 2 5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg> },
    ],
  },
  {
    id: 'elliott',
    title: 'Elliott Waves',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <path d="M2 15l3-4 2 2 3-8 3 5 2-3 3 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
        <text x="4" y="18" fill="currentColor" fontSize="6" fontWeight="bold" opacity="0.5">1</text>
      </svg>
    ),
    tools: [
      { id: 'elliottImpulse', title: 'Elliott Impulse (12345)', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M1 13l2-3 2 2 2-7 2 4 2-3 2 4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><text x="4" y="5" fill="currentColor" fontSize="5" opacity="0.7">3</text></svg> },
      { id: 'elliottCorrection', title: 'Elliott Correction (ABC)', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M3 4l4 7-3-3 4 5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><text x="2" y="5" fill="currentColor" fontSize="5" opacity="0.7">A</text></svg> },
      { id: 'elliottTriangle', title: 'Elliott Triangle (ABCDE)', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 10l3-6 2 4 3-3 2 3 2-1" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M2 10l12-4" fill="none" stroke="currentColor" strokeWidth="0.7" strokeDasharray="2 2" opacity="0.4"/></svg> },
      { id: 'elliottDouble', title: 'Elliott Double Combo (WXY)', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 5l3 6 3-4 3 5 3-6" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><text x="1" y="6" fill="currentColor" fontSize="5" opacity="0.7">W</text></svg> },
      { id: 'elliottTriple', title: 'Elliott Triple Combo (WXYXZ)', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M1 5l2 5 2-3 2 4 2-3 2 4 2-5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><text x="0" y="6" fill="currentColor" fontSize="5" opacity="0.7">W</text></svg> },
    ],
  },
  {
    id: 'gann',
    title: 'Gann',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <rect x="3" y="3" width="14" height="14" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2"/>
        <line x1="3" y1="3" x2="17" y2="17" stroke="currentColor" strokeWidth="1"/>
        <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
        <line x1="10" y1="3" x2="10" y2="17" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
      </svg>
    ),
    tools: [
      { id: 'gannBox', title: 'Gann Box', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="2" y="2" width="12" height="12" rx="0.5" fill="none" stroke="currentColor" strokeWidth="1.2"/><line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="8" y1="2" x2="8" y2="14" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="2" y1="2" x2="14" y2="14" stroke="currentColor" strokeWidth="0.8"/></svg> },
      { id: 'gannFan', title: 'Gann Fan', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="14" x2="14" y2="2" stroke="currentColor" strokeWidth="1.2"/><line x1="2" y1="14" x2="14" y2="6" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="2" y1="14" x2="14" y2="10" stroke="currentColor" strokeWidth="0.8" opacity="0.3"/><line x1="2" y1="14" x2="8" y2="2" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="2" y1="14" x2="5" y2="2" stroke="currentColor" strokeWidth="0.8" opacity="0.3"/></svg> },
      { id: 'gannSquare', title: 'Gann Square', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="2" y="2" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.2"/><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/><line x1="2" y1="2" x2="14" y2="14" stroke="currentColor" strokeWidth="0.8"/><line x1="14" y1="2" x2="2" y2="14" stroke="currentColor" strokeWidth="0.8"/></svg> },
    ],
  },
  {
    id: 'cycles',
    title: 'Cycles',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
      </svg>
    ),
    tools: [
      { id: 'cycle', title: 'Cycle Circle', icon: <svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.2"/><circle cx="8" cy="8" r="1.5" fill="currentColor"/><line x1="8" y1="5" x2="8" y2="11" stroke="currentColor" strokeWidth="0.7" opacity="0.3"/><line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="0.7" opacity="0.3"/></svg> },
      { id: 'cycleLines', title: 'Cycle Lines', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="3" y1="1" x2="3" y2="15" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><line x1="8" y1="1" x2="8" y2="15" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><line x1="13" y1="1" x2="13" y2="15" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><path d="M5 6.5L3 8l2 1.5M6 6.5l2 1.5-2 1.5" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/></svg> },
      { id: 'sineWave', title: 'Sine Wave', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M1 8 C3 3, 5 3, 8 8 S13 13, 15 8" fill="none" stroke="currentColor" strokeWidth="1.3"/><line x1="1" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="0.6" strokeDasharray="2 2" opacity="0.3"/></svg> },
    ],
  },
  {
    id: 'forecasting',
    title: 'Forecasting',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <path d="M3 14l4-4 3 2 7-8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M14 4h3v3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    tools: [
      { id: 'longPosition', title: 'Long Position', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="3" y="2" width="10" height="5" rx="1" fill="none" stroke="#22c55e" strokeWidth="1.2"/><rect x="3" y="9" width="10" height="5" rx="1" fill="none" stroke="#ef4444" strokeWidth="1.2"/><line x1="3" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.5"/><polygon points="8,3 6,5.5 10,5.5" fill="#22c55e"/></svg> },
      { id: 'shortPosition', title: 'Short Position', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="3" y="2" width="10" height="5" rx="1" fill="none" stroke="#ef4444" strokeWidth="1.2"/><rect x="3" y="9" width="10" height="5" rx="1" fill="none" stroke="#22c55e" strokeWidth="1.2"/><line x1="3" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.5"/><polygon points="8,13 6,10.5 10,10.5" fill="#ef4444"/></svg> },
      { id: 'forecast', title: 'Forecast', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 12l3-3 2 1.5 4-5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 5.5h3v3" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M11 5.5l3-3" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/></svg> },
      { id: 'dateRange', title: 'Date Range', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="3" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><line x1="13" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><line x1="3" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.5"/><path d="M5 6.5L3 8l2 1.5M11 6.5l2 1.5-2 1.5" fill="none" stroke="currentColor" strokeWidth="1.2"/></svg> },
      { id: 'datePriceRange', title: 'Date & Price Range', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="3" y="3" width="10" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2" strokeDasharray="3 2"/><line x1="3" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="0.8"/><line x1="8" y1="3" x2="8" y2="13" stroke="currentColor" strokeWidth="0.8"/><path d="M3 13l10-10" stroke="currentColor" strokeWidth="1" opacity="0.5"/></svg> },
      { id: 'barsPattern', title: 'Bars Pattern', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 11l2-3 2 1 2-4 2 2 2-1 2-2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="3 2"/><path d="M2 11l2-3 2 1 2-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> },
      { id: 'ghostFeed', title: 'Ghost Feed', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="3" y="5" width="2" height="4" rx="0.5" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="0.5" opacity="0.3"/><rect x="6" y="4" width="2" height="5" rx="0.5" fill="currentColor" opacity="0.25" stroke="currentColor" strokeWidth="0.5" opacity="0.35"/><rect x="9" y="6" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.3" stroke="currentColor" strokeWidth="0.5" opacity="0.4"/><rect x="12" y="3" width="2" height="6" rx="0.5" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="0.5" opacity="0.25"/></svg> },
    ],
  },
  {
    id: 'measure',
    title: 'Measure',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <rect x="4" y="5" width="12" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 2"/>
        <line x1="10" y1="5" x2="10" y2="15" stroke="currentColor" strokeWidth="1"/>
        <path d="M8 6.5l2-2 2 2M8 13.5l2 2 2-2" stroke="currentColor" strokeWidth="1" fill="none"/>
      </svg>
    ),
    tools: [
      { id: 'measure', title: 'Price Range / Measure', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="3" y="4" width="10" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><line x1="8" y1="4" x2="8" y2="12" stroke="currentColor" strokeWidth="1"/><path d="M6 5l2-2 2 2M6 11l2 2 2-2" stroke="currentColor" strokeWidth="1" fill="none"/></svg> },
    ],
  },
  {
    id: 'text',
    title: 'Text & Notes',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <text x="4" y="15" fill="currentColor" fontSize="14" fontWeight="bold" fontFamily="-apple-system,sans-serif">T</text>
      </svg>
    ),
    tools: [
      { id: 'textLabel', title: 'Text', icon: <svg width="16" height="16" viewBox="0 0 16 16"><text x="3" y="12" fill="currentColor" fontSize="12" fontWeight="bold" fontFamily="-apple-system,sans-serif">T</text></svg> },
      { id: 'anchoredText', title: 'Anchored Text', icon: <svg width="16" height="16" viewBox="0 0 16 16"><circle cx="3" cy="8" r="2" fill="currentColor"/><line x1="5" y1="8" x2="8" y2="8" stroke="currentColor" strokeWidth="1"/><text x="8" y="11" fill="currentColor" fontSize="9" fontFamily="-apple-system,sans-serif">Ab</text></svg> },
      { id: 'note', title: 'Note', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="2" y="3" width="12" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2"/><line x1="2" y1="5.5" x2="14" y2="5.5" stroke="currentColor" strokeWidth="1.5" opacity="0.6"/><line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/><line x1="4" y1="10" x2="10" y2="10" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/></svg> },
      { id: 'callout', title: 'Callout', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="5" y="2" width="9" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2"/><path d="M7 9l-4 4v-3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><line x1="7" y1="5" x2="12" y2="5" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="7" y1="7" x2="11" y2="7" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/></svg> },
      { id: 'priceLabel', title: 'Price Label', icon: <svg width="16" height="16" viewBox="0 0 16 16"><line x1="2" y1="8" x2="10" y2="8" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2"/><rect x="10" y="5" width="5" height="6" rx="1" fill="currentColor" opacity="0.8"/><text x="11" y="10" fill="#1e1e2e" fontSize="6" fontWeight="bold" fontFamily="-apple-system,sans-serif">$</text></svg> },
    ],
  },
  {
    id: 'markers',
    title: 'Markers',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20">
        <polygon points="10,3 15,11 5,11" fill="currentColor" opacity="0.7"/>
      </svg>
    ),
    tools: [
      { id: 'arrowUp', title: 'Arrow Up', icon: <svg width="16" height="16" viewBox="0 0 16 16"><polygon points="8,2 13,10 3,10" fill="currentColor" opacity="0.8"/></svg> },
      { id: 'arrowDown', title: 'Arrow Down', icon: <svg width="16" height="16" viewBox="0 0 16 16"><polygon points="8,14 13,6 3,6" fill="currentColor" opacity="0.8"/></svg> },
    ],
  },
];

const CLICK_COUNT = {
  hline: 1, vline: 1, arrowUp: 1, arrowDown: 1, textLabel: 1, anchoredText: 1, note: 1, priceLabel: 1,
  trendline: 2, ray: 2, rectangle: 2, ellipse: 2, fibonacci: 2,
  fibFan: 2, fibArcs: 2, fibTimeZones: 2, measure: 2,
  longPosition: 2, shortPosition: 2, dateRange: 2, datePriceRange: 2,
  callout: 2, cycle: 2, cycleLines: 2, sineWave: 2,
  arc: 2, arrowLine: 2, circle: 2, filledRect: 2, filledEllipse: 2,
  brush: 'freehand', highlighter: 'freehand',
  polyline: 'multi',
  gannBox: 2, gannFan: 2, gannSquare: 2, regressionTrend: 2,
  channel: 3, pitchfork: 3, triangle: 3, fibExtension: 3, flatChannel: 3,
  disjointChannel: 'fixed:4',
  abcPattern: 'fixed:3', triPattern: 'fixed:3',
  elliottCorrection: 'fixed:4', elliottDouble: 'fixed:5',
  xabcd: 'fixed:5', cypherPattern: 'fixed:5', elliottTriangle: 'fixed:6',
  elliottTriple: 'fixed:7', threeDrives: 'fixed:7',
  headShoulders: 'fixed:7', elliottImpulse: 'fixed:6',
  forecast: 'multi', barsPattern: 'multi', ghostFeed: 'multi',
};

const FIB_TYPES_WITH_LEVELS = ['fibonacci', 'fibExtension'];

function getHint(tool, step) {
  const clicks = CLICK_COUNT[tool] || 2;
  if (clicks === 'freehand') return 'Click and drag to draw';
  if (clicks === 'multi') return step === 0 ? 'Click to start — double-click to finish' : `Point ${step + 1} — double-click to finish`;
  if (typeof clicks === 'string' && clicks.startsWith('fixed:')) {
    const total = parseInt(clicks.split(':')[1]);
    return `Point ${step + 1} of ${total}`;
  }
  if (tool === 'textLabel' || tool === 'anchoredText' || tool === 'note') return 'Click to place — type text after';
  if (tool === 'priceLabel') return 'Click at price level';
  if (tool === 'callout') return step === 0 ? 'Click anchor point' : 'Click bubble position';
  if (clicks === 1) return 'Click to place';
  if (tool === 'longPosition' || tool === 'shortPosition') return step === 0 ? 'Click entry price' : 'Click stop loss';
  if (clicks === 2) return step === 0 ? 'Click first point' : 'Click second point';
  return step === 0 ? 'Click first point' : step === 1 ? 'Click second point' : 'Click third point';
}

export default function DrawingSidebar({
  activeTool, onSelectTool, onCancel, onClearAll,
  drawings, onRemoveDrawing,
  drawingColor, onColorChange,
  pendingPoint, pendingPoints, selectedId,
  onUpdateLevels, onGetLevels,
  magnetMode, onMagnetChange, onToggleLock, onLoadDrawingTemplate,
}) {
  const [openGroupId, setOpenGroupId] = useState(null);
  const [showFibEditor, setShowFibEditor] = useState(false);
  const [showDrawingsList, setShowDrawingsList] = useState(false);
  const timeoutRef = useRef(null);

  const selectedDrawing = selectedId != null ? drawings.find((d) => d.id === selectedId) : null;
  const hasFibLevels = selectedDrawing && FIB_TYPES_WITH_LEVELS.includes(selectedDrawing.type);
  const step = pendingPoints?.length || (pendingPoint ? 1 : 0);

  // Which tool is the "last used" per group — show its icon on the group button
  const [lastUsedPerGroup, setLastUsedPerGroup] = useState({});

  const handleSelectTool = useCallback((toolId) => {
    // Find which group this tool belongs to
    for (const g of TOOL_GROUPS) {
      if (g.tools.some((t) => t.id === toolId)) {
        setLastUsedPerGroup((prev) => ({ ...prev, [g.id]: toolId }));
        break;
      }
    }
    onSelectTool(activeTool === toolId ? null : toolId);
    setOpenGroupId(null);
  }, [activeTool, onSelectTool]);

  const handleGroupEnter = useCallback((groupId) => {
    clearTimeout(timeoutRef.current);
    setOpenGroupId(groupId);
  }, []);

  const handleGroupLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => setOpenGroupId(null), 200);
  }, []);

  const handleFibLevelsChange = useCallback((levels) => {
    if (selectedId != null && onUpdateLevels) onUpdateLevels(selectedId, levels);
  }, [selectedId, onUpdateLevels]);

  // Close fib editor when selection changes
  useEffect(() => { setShowFibEditor(false); }, [selectedId]);

  return (
    <div className="ds">
      {/* ── Tool groups ── */}
      <div className="ds-tools">
        {TOOL_GROUPS.map((group) => {
          const isGroupActive = group.tools.some((t) => t.id === activeTool);
          const lastUsed = lastUsedPerGroup[group.id];
          const displayTool = group.tools.find((t) => t.id === (lastUsed || activeTool)) || group.tools[0];
          const isOpen = openGroupId === group.id;

          return (
            <div
              key={group.id}
              className="ds-group"
              onMouseEnter={() => handleGroupEnter(group.id)}
              onMouseLeave={handleGroupLeave}
            >
              <button
                className={`ds-btn ${isGroupActive ? 'ds-btn-active' : ''}`}
                onClick={() => handleSelectTool(displayTool.id)}
                title={group.title}
              >
                {displayTool.icon || group.icon}
                {group.tools.length > 1 && <span className="ds-chevron" />}
              </button>

              {/* Flyout submenu */}
              {isOpen && group.tools.length > 1 && (
                <div className="ds-flyout" onMouseEnter={() => handleGroupEnter(group.id)} onMouseLeave={handleGroupLeave}>
                  <div className="ds-flyout-title">{group.title}</div>
                  {group.tools.map((tool) => (
                    <button
                      key={tool.id ?? 'cursor'}
                      className={`ds-flyout-item ${activeTool === tool.id ? 'ds-flyout-active' : ''}`}
                      onClick={() => handleSelectTool(tool.id)}
                    >
                      {tool.icon}
                      <span>{tool.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Separator ── */}
      <div className="ds-sep" />

      {/* ── Color picker ── */}
      <label className="ds-color" title="Drawing color">
        <input type="color" value={drawingColor} onChange={(e) => onColorChange(e.target.value)} className="ds-color-input" />
        <span className="ds-color-swatch" style={{ background: drawingColor }} />
      </label>

      {/* ── Magnet toggle ── */}
      <div className="ds-group" onMouseEnter={() => handleGroupEnter('_magnet')} onMouseLeave={handleGroupLeave}>
        <button
          className={`ds-btn ${magnetMode !== 'off' ? 'ds-btn-magnet-on' : ''}`}
          onClick={() => onMagnetChange?.(magnetMode === 'off' ? 'weak' : magnetMode === 'weak' ? 'strong' : 'off')}
          title={`Magnet: ${magnetMode === 'off' ? 'Off' : magnetMode === 'weak' ? 'Weak (close)' : 'Strong (OHLC)'}`}
        >
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path d="M4 2v4a4 4 0 008 0V2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="4" y1="2" x2="4" y2="4" stroke={magnetMode !== 'off' ? '#6366f1' : 'currentColor'} strokeWidth="3" strokeLinecap="round"/>
            <line x1="12" y1="2" x2="12" y2="4" stroke={magnetMode !== 'off' ? '#6366f1' : 'currentColor'} strokeWidth="3" strokeLinecap="round"/>
          </svg>
          {magnetMode === 'strong' && <span style={{ position: 'absolute', bottom: 2, right: 2, width: 5, height: 5, borderRadius: '50%', background: '#6366f1' }} />}
        </button>
        {openGroupId === '_magnet' && (
          <div className="ds-flyout" onMouseEnter={() => handleGroupEnter('_magnet')} onMouseLeave={handleGroupLeave}>
            <div className="ds-flyout-title">Magnet Mode</div>
            <button className={`ds-flyout-item ${magnetMode === 'off' ? 'ds-flyout-active' : ''}`} onClick={() => { onMagnetChange?.('off'); setOpenGroupId(null); }}>
              <svg width="14" height="14" viewBox="0 0 16 16"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <span>Off</span>
            </button>
            <button className={`ds-flyout-item ${magnetMode === 'weak' ? 'ds-flyout-active' : ''}`} onClick={() => { onMagnetChange?.('weak'); setOpenGroupId(null); }}>
              <svg width="14" height="14" viewBox="0 0 16 16"><path d="M4 3v4a4 4 0 008 0V3" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
              <span>Weak Magnet (Close)</span>
            </button>
            <button className={`ds-flyout-item ${magnetMode === 'strong' ? 'ds-flyout-active' : ''}`} onClick={() => { onMagnetChange?.('strong'); setOpenGroupId(null); }}>
              <svg width="14" height="14" viewBox="0 0 16 16"><path d="M4 3v4a4 4 0 008 0V3" fill="none" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round"/><line x1="4" y1="3" x2="4" y2="5" stroke="#6366f1" strokeWidth="3" strokeLinecap="round"/><line x1="12" y1="3" x2="12" y2="5" stroke="#6366f1" strokeWidth="3" strokeLinecap="round"/></svg>
              <span>Strong Magnet (OHLC)</span>
            </button>
          </div>
        )}
      </div>

      <div className="ds-sep" />

      {/* ── Utility buttons ── */}
      {activeTool && (
        <button className="ds-btn ds-btn-cancel" onClick={onCancel} title="Cancel (Esc)">
          <svg width="16" height="16" viewBox="0 0 16 16"><path d="M11 5L5 11M5 5l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>
      )}

      {hasFibLevels && (
        <button
          className={`ds-btn ${showFibEditor ? 'ds-btn-active' : ''}`}
          onClick={() => setShowFibEditor(!showFibEditor)}
          title="Edit Fib levels"
        >
          <svg width="16" height="16" viewBox="0 0 16 16"><path d="M12 2l2 2-8 8H4v-2l8-8z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>
        </button>
      )}

      {selectedDrawing && onToggleLock && (
        <button className={`ds-btn ${selectedDrawing.locked ? 'ds-btn-active' : ''}`}
          onClick={() => onToggleLock(selectedId)} title={selectedDrawing.locked ? 'Unlock' : 'Lock'}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
            {selectedDrawing.locked
              ? <path d="M4 7V5a4 4 0 018 0v2m-8 0h8a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V8a1 1 0 011-1z"/>
              : <path d="M11 7V5a3 3 0 00-6 0m-1 2h8a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V8a1 1 0 011-1z"/>}
          </svg>
        </button>
      )}
      {selectedDrawing && (
        <button className="ds-btn ds-btn-del" onClick={() => onRemoveDrawing(selectedId)} title="Delete selected">
          <svg width="16" height="16" viewBox="0 0 16 16"><path d="M5.5 5.5v5m5-5v5M3 3.5h10l-.7 8.4a1.5 1.5 0 01-1.5 1.35H5.2a1.5 1.5 0 01-1.5-1.35L3 3.5zm-1 0h12M6.5 3.5v-1a1 1 0 011-1h1a1 1 0 011 1v1" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      )}

      {drawings.length > 0 && (
        <button className="ds-btn ds-btn-clear" onClick={onClearAll} title="Clear all">
          <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>
      )}

      {/* Save/Load drawing templates */}
      {drawings.length > 0 && (
        <button className="ds-btn" title="Save drawings as template" onClick={() => {
          const name = prompt('Template name:');
          if (!name) return;
          const templates = JSON.parse(localStorage.getItem('drawingTemplates') || '[]');
          const data = drawings.map(d => {
            const p = d.primitive;
            return { type: d.type, label: d.label, color: p.color,
              points: p.points || null, pt1: p.pt1 || null, pt2: p.pt2 || null, pt3: p.pt3 || null,
              price: p.price ?? null, logical: p.logical ?? null,
              text: p.text || null, direction: p.direction || null,
              strokeWidth: p.strokeWidth || null, isHighlighter: p.isHighlighter || null,
              filled: p.filled || null, extend: p.extend || null,
            };
          });
          templates.push({ name, drawings: data, date: new Date().toISOString() });
          localStorage.setItem('drawingTemplates', JSON.stringify(templates));
        }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
            <path d="M12 2l2 2-8 8H4v-2l8-8zM10 4l2 2"/>
          </svg>
        </button>
      )}
      <button className="ds-btn" title="Load drawing template" onClick={() => {
        const templates = JSON.parse(localStorage.getItem('drawingTemplates') || '[]');
        if (templates.length === 0) { alert('No saved templates'); return; }
        const names = templates.map((t, i) => `${i + 1}. ${t.name}`).join('\n');
        const choice = prompt(`Choose template:\n${names}`);
        if (!choice) return;
        const idx = parseInt(choice) - 1;
        if (idx >= 0 && idx < templates.length && onLoadDrawingTemplate) {
          onLoadDrawingTemplate(templates[idx].drawings);
        }
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
          <path d="M2 12h12M8 2v7m-3-3l3 3 3-3"/>
        </svg>
      </button>

      {/* ── Spacer ── */}
      <div className="ds-spacer" />

      {/* ── Drawing count / list toggle ── */}
      {drawings.length > 0 && (
        <button className="ds-btn" onClick={() => setShowDrawingsList(v => !v)}
          title={`${drawings.length} drawings — click to list`}>
          <span style={{ fontSize: '10px', fontWeight: 700 }}>{drawings.length}</span>
        </button>
      )}

      {/* ── Drawings list flyout ── */}
      {showDrawingsList && drawings.length > 0 && (
        <div className="ds-flyout" style={{ bottom: 0, top: 'auto', left: '100%', maxHeight: '300px', overflow: 'auto' }}>
          <div className="ds-flyout-title">Drawings ({drawings.length})</div>
          {drawings.map(d => (
            <div key={d.id} className={`ds-flyout-item ${d.id === selectedId ? 'ds-flyout-active' : ''}`}
              style={{ justifyContent: 'space-between' }}>
              <span>
                {d.locked ? '🔒 ' : ''}{d.label || d.type}
              </span>
              <button style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: '11px' }}
                onClick={(e) => { e.stopPropagation(); onRemoveDrawing(d.id); }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Hint overlay (appears at top of chart) ── */}
      {activeTool && (
        <div className="ds-hint">
          <span className="ds-hint-dot" />
          {getHint(activeTool, step)}
        </div>
      )}

      {/* ── Fib Levels Editor flyout ── */}
      {showFibEditor && hasFibLevels && (
        <div className="ds-fib-flyout">
          <FibLevelsEditor
            key={selectedId}
            initialLevels={onGetLevels?.(selectedId) || []}
            onChange={handleFibLevelsChange}
            onClose={() => setShowFibEditor(false)}
          />
        </div>
      )}
    </div>
  );
}
